import React, { useMemo } from 'react';
import classNames from 'classnames';
import { useSelector, useDispatch } from 'react-redux';
import { useIntl } from 'react-intl';
import styles from './index.scss';
import stylesV2 from './notification-banner.module.css';
import * as actions from './data/actions';
import * as selectors from './data/selectors';
import {
  BannerMessage,
  NotificationBannerType,
  TranslatedBannerMessage,
} from './data/types';
import { useIsLastMountedInstanceOf } from 'signer-app/utils/use-is-last-mounted-instance-of';
import { Banner } from '@dropbox/dig-components/banner';
import { Box } from '@dropbox/dig-foundations';

type BannerProps = {
  message: BannerMessage | TranslatedBannerMessage;
  onClose: (id: BannerMessage['guid']) => void;
};

export const ONE_TIME_USE_NOTIFICATION_GUID =
  'one-time-use-notification-banner-guid';

export function useNotificationTimeout(
  { guid, timeout }: BannerMessage | TranslatedBannerMessage,
  onClose: (id: BannerMessage['guid']) => void,
) {
  React.useEffect(() => {
    if (timeout) {
      const handle = setTimeout(() => onClose(guid), timeout);
      return () => clearTimeout(handle);
    }
    return () => {};
  }, [guid, onClose, timeout]);
}

export function useNotificationText(
  message: BannerMessage | TranslatedBannerMessage,
) {
  const intl = useIntl();
  return 'messageDescriptor' in message
    ? intl.formatMessage(message.messageDescriptor, message.values || {})
    : message.translatedMessage;
}

export function NotificationBanner({ message, onClose }: BannerProps) {
  useNotificationTimeout(message, onClose);
  const text = useNotificationText(message);

  const bannerType = useMemo(() => {
    switch (message.type) {
      case NotificationBannerType.Err:
        return 'alert';
      case NotificationBannerType.Info:
        return 'attention';
      case NotificationBannerType.Success:
        return 'success';
      case NotificationBannerType.Warning:
        return 'warning';
      default:
        return 'alert';
    }
  }, [message.type]);

  const shouldShowOnTop = useMemo(() => {
    return message.guid === ONE_TIME_USE_NOTIFICATION_GUID;
  }, [message.guid]);

  return (
    <Box
      marginX="auto"
      marginTop="Micro Small"
      className={classNames(stylesV2.banner, {
        [stylesV2.showOnTheTop]: shouldShowOnTop,
      })}
      data-qa-ref="notification-wrapper"
      data-testid="notification-wrapper"
    >
      <Banner
        type={bannerType}
        withCloseButton={`notification-${message.type}-close`}
        onRequestClose={() => onClose(message.guid)}
      >
        <Banner.Message>
          <span data-qa-ref="validation-error" data-testid="validation-error">
            {text}
          </span>
        </Banner.Message>
      </Banner>
    </Box>
  );
}

export { NotificationBannerType };
type NotificationListProps = {
  className?: string;
  enableTimeOut?: boolean;
};
// Inline and snackbar lists register under NotificationList, so they share one
// stack and only the last-mounted list of either kind shows the messages.
export function useNotificationMessages(enableTimeOut?: boolean) {
  const dispatch = useDispatch();
  const isActive = useIsLastMountedInstanceOf(NotificationList);

  const messages = useSelector(selectors.getBannerMessages);

  const onClose = React.useCallback(
    (id: BannerMessage['guid']) => {
      dispatch(actions.removeBannerMessage(id));
    },
    [dispatch],
  );

  React.useEffect(() => {
    const timeOut = setTimeout(() => {
      if (enableTimeOut) {
        messages.forEach((message) => onClose(message.guid));
      }
    }, 4000);

    return () => {
      clearTimeout(timeOut);
    };
  }, [enableTimeOut, messages, onClose]);

  return { isActive, messages, onClose };
}

export default function NotificationList({
  className,
  enableTimeOut,
}: NotificationListProps) {
  const { isActive, messages, onClose } =
    useNotificationMessages(enableTimeOut);

  if (!isActive) {
    return null;
  }
  const messageBanners = messages.map((message) => {
    return (
      <NotificationBanner
        key={message.key || message.guid}
        message={message}
        onClose={onClose}
      />
    );
  });
  return (
    <div className={classNames(styles.container, className)}>
      {messageBanners}
    </div>
  );
}
